"use client";

import Link from "next/link";
import { ArrowRight, TrendingUp, Copy as CopyIcon } from "lucide-react";
import GradientAvatar from "@/app/components/GradientAvatar";
import { useT } from "@/hooks/useT";

interface Trader {
  user_id?: string;
  wallet?: string;
  address?: string;
  username?: string;
  name?: string;
  display_name?: string;
  image?: string;
  avatar?: string;
  profile_image?: string;
  pnl_30d?: number;
  pnl?: number;
  roi?: number;
  winrate?: number;
  win_rate?: number;
  copiers_count?: number;
  followers?: number;
  total_trades?: number;
}

interface WhosWinningProps {
  initialTraders?: Trader[];
}

/**
 * Who's Winning — proof that our leaderboards aren't empty.
 *
 * Replaces TopMovers + SmartActivity sections. Top 6 traders surfaced from
 * the /api/social/traders/most-copied endpoint, each with a one-click
 * "copy" affordance that deep-links to the copy-trader modal on the
 * target trader's profile. Everything here is meant to close the loop:
 * see who's winning → click → start copying.
 */
export default function WhosWinning({ initialTraders = [] }: WhosWinningProps) {
  const t = useT();
  const traders = initialTraders.slice(0, 6);

  return (
    <section className="py-20 lg:py-28 bg-gray-50 dark:bg-[#0f0f12]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
              {t("home.winning.eyebrow")}
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
              {t("home.winning.title")}
            </h2>
            <p className="mt-3 text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg">
              {t("home.winning.body")}
            </p>
          </div>
          <Link
            href="/traders"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-inter-tight font-medium text-sm hover:bg-white dark:hover:bg-white/[0.04] transition-colors self-start lg:self-auto"
          >
            {t("home.winning.cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Traders grid */}
        {traders.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {traders.map((t, i) => (
              <TraderCard key={t.user_id || t.wallet || t.address || i} trader={t} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-[120px] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-white/[0.02]"
                aria-hidden
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TraderCard({ trader }: { trader: Trader }) {
  const t = useT();
  const wallet = trader.wallet || trader.address || trader.user_id || "";
  const name =
    trader.username ||
    trader.display_name ||
    trader.name ||
    (wallet.startsWith("0x") ? `${wallet.slice(0, 6)}…${wallet.slice(-4)}` : t("home.card.traderFallback"));
  const avatar = trader.image || trader.avatar || trader.profile_image;
  const pnl = Number(trader.pnl_30d ?? trader.pnl ?? 0);
  const winrate = Number(trader.win_rate ?? trader.winrate ?? 0);
  const copiers = Number(trader.copiers_count ?? trader.followers ?? 0);
  const positivePnl = pnl > 0;

  const href = trader.username ? `/traders/@${trader.username}` : wallet ? `/traders/${wallet}` : "/traders";

  return (
    <Link
      href={href}
      prefetch={false}
      className="group block rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] p-4 hover:border-gray-300 dark:hover:border-white/20 transition-colors"
    >
      <div className="flex items-center gap-3">
        {avatar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatar}
            alt={name || "Trader avatar"}
            className="w-12 h-12 rounded-[5px] object-cover flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <GradientAvatar seed={wallet || name} size={48} className="!rounded-[5px] flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-inter-tight font-semibold text-[15px] text-gray-900 dark:text-white truncate">
              {name}
            </span>
          </div>
          <div className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
            {copiers > 0 ? (
              <>
                <CopyIcon className="w-3 h-3" />
                <span>{t("home.winning.copiers", { count: copiers })}</span>
              </>
            ) : (
              <span>{trader.total_trades ? t("home.winning.trades", { count: trader.total_trades }) : t("home.card.traderFallback")}</span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div
            className={`font-inter-tight text-[16px] font-semibold tabular-nums ${
              positivePnl ? "text-emerald-600 dark:text-emerald-400" : pnl < 0 ? "text-red-500" : "text-gray-900 dark:text-white"
            }`}
          >
            {formatPnl(pnl)}
          </div>
          {winrate > 0 && (
            <div className="font-inter-tight text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
              {t("home.winning.winPct", { pct: Math.round(winrate * 100) })}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/[0.05]">
        <span className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">{t("home.card.pnl30d")}</span>
        <span className="font-inter-tight text-[11px] text-gray-900 dark:text-white group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          {t("home.winning.viewProfile")}
        </span>
      </div>
    </Link>
  );
}

function formatPnl(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "$0";
  const sign = n > 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
}
